import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { adminGet, adminDelete, adminPost, adminPut } from "../../api/adminClient";
import Icon from "../../components/AppIcon";
import { useToast } from "../../hooks/use-toast";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { InlineLoader } from "../../components/ui/Loader";

/** Normalize product-types API response: server may return data as array or as { items, total, stats, total_pages } */
function normalizeProductTypesResponse(data) {
  if (Array.isArray(data)) {
    const items = data;
    const active = items.filter((t) => t.is_active).length;
    return {
      items,
      total: items.length,
      total_pages: 1,
      stats: { total_types: items.length, active_types: active, inactive_or_deleted_types: items.length - active },
    };
  }
  const items = Array.isArray(data?.items) ? data.items : [];
  return {
    items,
    total: typeof data?.total === "number" ? data.total : items.length,
    total_pages: typeof data?.total_pages === "number" ? data.total_pages : 1,
    stats: data?.stats ?? null,
  };
}

function slugify(s) {
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

const defaultForm = {
  name: "",
  slug: "",
  parent_id: "",
  main_category: "Endoscopy",
  description: "",
  sort_order: 0,
  is_active: true,
};

const ProductTypesList = () => {
  const { success, error: showError } = useToast();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive | deleted
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("status", statusFilter || "all");
      if (search && search.trim()) {
        params.set("search", search.trim());
      }
      params.set("page", String(page));
      params.set("per_page", String(perPage));

      const raw = await adminGet(
        `/admin/products/product-types?${params.toString()}`
      );
      const { items, total, total_pages, stats } = normalizeProductTypesResponse(raw ?? {});
      setTypes(items);
      setStats(stats);
      setTotalCount(total);
      setTotalPages(total_pages);
    } catch (e) {
      if (e?.status !== 401 && !e?.isSessionExpired) setError(e?.message || "Failed to load product types");
      if (e?.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await load();
      if (initialLoading) {
        setInitialLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, search]);

  // Debounce search; only trigger API when >= 3 chars or cleared
  useEffect(() => {
    const trimmed = searchInput.trim();
    const handler = setTimeout(() => {
      if (trimmed === "") {
        setPage(1);
        setSearch("");
      } else if (trimmed.length >= 3) {
        setPage(1);
        setSearch(trimmed);
      }
      // 1–2 chars: do not change current search term
    }, 350);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const validateForm = () => {
    const err = {};
    if (!form.name?.trim()) {
      err.name = "Name is required";
    } else if (form.name.trim().length < 2) {
      err.name = "Name must be at least 2 characters";
    }
    if (!form.slug?.trim()) {
      err.slug = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(form.slug.trim())) {
      err.slug = "Slug can only contain lowercase letters, numbers, and hyphens";
    }
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleFieldChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "name" && !editingType) {
      setForm((f) => ({ ...f, slug: slugify(value) }));
    }
    if (formErrors[key]) {
      setFormErrors((e) => {
        const next = { ...e };
        delete next[key];
        return next;
      });
    }
  };

  const openAddModal = () => {
    setEditingType(null);
    setForm(defaultForm);
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (pt) => {
    setEditingType(pt);
    setForm({
      name: pt.name ?? "",
      slug: pt.slug ?? "",
      parent_id: pt.parent_id ?? "",
      main_category: pt.main_category ?? "Endoscopy",
      description: pt.description ?? "",
      sort_order: pt.sort_order ?? 0,
      is_active: pt.is_active ?? true,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingType(null);
    setForm(defaultForm);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowModal(false);
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim().toLowerCase().replace(/\s+/g, "-"),
        parent_id: form.parent_id ? Number(form.parent_id) : null,
        main_category: form.main_category,
        description: form.description?.trim() || null,
        sort_order: Number(form.sort_order) || 0,
        is_active: Boolean(form.is_active),
      };
      if (editingType) {
        await adminPut(`/admin/products/product-types/${editingType.product_type_id}`, payload);
      } else {
        await adminPost("/admin/products/product-types", payload);
      }
      closeModal();
      success(editingType ? "Product type updated successfully" : "Product type created successfully");
      await load();
    } catch (err) {
      setFormErrors((e) => ({ ...e, _submit: err?.message || "Save failed" }));
      setShowModal(true);
      showError(err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (pt) => {
    setTypeToDelete(pt);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!typeToDelete) return;
    setDeleteModalOpen(false);
    setLoading(true);
    try {
      await adminDelete(`/admin/products/product-types/${typeToDelete.product_type_id}`);
      setTypeToDelete(null);
      success("Product type deleted successfully");
      await load();
    } catch (e) {
      showError(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () =>
    form.name?.trim() &&
    form.name.trim().length >= 2 &&
    form.slug?.trim() &&
    /^[a-z0-9-]+$/.test(form.slug.trim());

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Product Types</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage product categories</p>
        </div>
        <div className="bg-card rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="flex flex-col items-center justify-center py-12">
            <InlineLoader text="Loading product types…" size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Product Types</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage product categories</p>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 shadow-md hover:shadow-lg transition-shadow shrink-0"
        >
          <Icon name="Plus" size={20} />
          Add Category
        </button>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="bg-card rounded-xl shadow-lg border border-border p-4 sm:p-6 space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-secondary">Total Categories</p>
              <p className="mt-1 text-2xl font-bold text-text-primary">{stats?.total_types ?? totalCount}</p>
            </div>
            <div className="w-11 h-11 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
              <Icon name="FolderTree" size={22} className="text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-secondary">Active</p>
              <p className="mt-1 text-2xl font-bold text-success">
                {stats?.active_types ?? types.filter((t) => t.is_active).length}
              </p>
            </div>
            <div className="w-11 h-11 rounded-lg bg-green-500 flex items-center justify-center shrink-0">
              <Icon name="CheckCircle" size={22} className="text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-secondary">Inactive / Deleted</p>
              <p className="mt-1 text-2xl font-bold text-warning">
                {stats?.inactive_or_deleted_types ?? (totalCount - types.filter((t) => t.is_active).length)}
              </p>
            </div>
            <div className="w-11 h-11 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
              <Icon name="Archive" size={22} className="text-white" />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex-1 flex items-center gap-3">
            <div className="relative w-full">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
                placeholder="Search by name or slug…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-full max-w-[220px]">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value || "all");
                }}
                className="w-full appearance-none rounded-lg border border-border bg-background px-3 py-2.5 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deleted">Deleted</option>
              </select>
              <Icon
                name="ChevronDown"
                size={16}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
            </div>
          </div>
        </div>
      </motion.div>
      {error && (
        <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-sm">
          {error}
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="bg-card rounded-xl shadow-lg border border-border overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-sm font-medium text-text-primary">Name</th>
                <th className="px-4 py-3 text-sm font-medium text-text-primary">Main Category</th>
                <th className="px-4 py-3 text-sm font-medium text-text-primary">Slug</th>
                <th className="px-4 py-3 text-sm font-medium text-text-primary text-center">Status</th>
                <th className="px-4 py-3 text-sm font-medium text-text-primary w-32 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8">
                    <div className="flex items-center justify-center">
                      <InlineLoader text="Loading categories…" size="medium" />
                    </div>
                  </td>
                </tr>
              ) : types.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-text-secondary">
                    No product types yet. Add one to get started.
                  </td>
                </tr>
              ) : (
                types.map((pt) => (
                  <tr key={pt.product_type_id} className="border-b border-border last:border-0 hover:bg-muted/5 transition-colors">
                    <td className="px-4 py-3 text-text-primary font-medium">
                      {pt.name}
                      {pt.parent_id && (
                        <div className="text-[10px] text-text-secondary mt-0.5 flex items-center gap-1 uppercase tracking-wider">
                          <Icon name="CornerDownRight" size={10} />
                          {types.find(t => t.product_type_id === pt.parent_id)?.name || 'Parent'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        pt.main_category === 'Surgery' 
                          ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20' 
                          : 'bg-primary/10 text-primary border border-primary/20'
                      }`}>
                        {pt.main_category || 'Endoscopy'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-sm">{pt.slug}</td>
                    <td className="px-4 py-3 text-center">
                      {pt.is_active ? (
                        <span className="inline-flex items-center text-xs font-medium text-success bg-success/10 px-2 py-1 rounded-full border border-success/20">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-medium text-text-secondary bg-muted px-2 py-1 rounded-full border border-border">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(pt)}
                          title="Edit"
                          className="p-2 rounded-lg hover:bg-muted text-primary transition-colors"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(pt)}
                          title="Delete"
                          className="p-2 rounded-lg hover:bg-muted text-error transition-colors"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border px-4 py-3">
          <p className="text-xs text-text-secondary">
            Showing{" "}
            {types.length === 0
              ? 0
              : (page - 1) * perPage + 1}{" "}
            –{" "}
            {(page - 1) * perPage + types.length} of {totalCount} categories
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1 text-sm text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
            >
              <Icon name="ChevronLeft" size={14} />
              Prev
            </button>
            <span className="text-xs text-text-secondary">
              Page {page} of {totalPages || 1}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => (totalPages ? Math.min(totalPages, p + 1) : p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1 text-sm text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
            >
              Next
              <Icon name="ChevronRight" size={14} />
            </button>
          </div>
        </div>
      </motion.div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[999]">
          <div
            className="bg-card rounded-xl shadow-lg border border-border p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-text-primary">
                {editingType ? "Edit Product Type" : "Add New Product Type"}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-muted text-text-secondary"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {formErrors._submit && (
              <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
                {formErrors._submit}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border bg-background text-text-primary ${
                    formErrors.name ? "border-error" : "border-border"
                  }`}
                />
                {formErrors.name && <p className="text-xs text-error mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Main Category *</label>
                <select
                  value={form.main_category}
                  onChange={(e) => handleFieldChange("main_category", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  required
                >
                  <option value="Endoscopy">Endoscopy</option>
                  <option value="Surgery">Surgery</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Parent Category (Optional)</label>
                <div className="relative">
                  <select
                    value={form.parent_id}
                    onChange={(e) => handleFieldChange("parent_id", e.target.value)}
                    className="w-full px-3 py-2.5 pr-9 appearance-none rounded-lg border border-border bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    <option value="">None (Top Level)</option>
                    {types
                      .filter(t => t.product_type_id !== editingType?.product_type_id && !t.parent_id && t.main_category === form.main_category)
                      .map(t => (
                        <option key={t.product_type_id} value={t.product_type_id}>{t.name}</option>
                      ))}
                  </select>
                  <Icon name="ChevronDown" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
                </div>
                <p className="text-[10px] text-text-secondary mt-1 italic">Note: Only top-level categories of the same Main Category are shown.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Slug *</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => handleFieldChange("slug", e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border bg-background text-text-primary ${
                    formErrors.slug ? "border-error" : "border-border"
                  }`}
                  placeholder="e.g. surgical-instruments"
                />
                {formErrors.slug && <p className="text-xs text-error mt-1">{formErrors.slug}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleFieldChange("description", e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Sort order</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => handleFieldChange("sort_order", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                  />
                </div>
                <div className="flex items-center pt-8">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => handleFieldChange("is_active", e.target.checked)}
                      className="rounded border-border"
                    />
                    <span className="text-sm text-text-primary">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-lg border border-border text-text-primary hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid()}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingType ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen && !!typeToDelete}
        title="Delete Product Type"
        message={`Are you sure you want to soft delete "${typeToDelete?.name}"? Products under it must be moved or deleted first.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setTypeToDelete(null); }}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default ProductTypesList;
